<template>
  <div class="dqm">

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="dqm__header">
      <div class="dqm__title">Исполнительные листы — очередь формирования</div>
      <div class="dqm__header-actions">
        <q-btn flat dense color="grey-6" icon="filter_alt_off" label="Сбросить"
          @click="clearFilters" />
        <q-btn outline color="primary" icon="refresh" label="Обновить"
          :loading="loading" @click="reload" />
      </div>
    </div>

    <!-- ── Stat cards ──────────────────────────────────────────── -->
    <div class="dqm__stats">
      <div
        v-for="card in statCards" :key="card.status"
        class="dqm__stat-card"
        :class="[`dqm__stat-card--${card.cls}`, { 'dqm__stat-card--active': filterStatus === card.status }]"
        @click="toggleStatusFilter(card.status)"
      >
        <q-icon :name="card.icon" size="18px" class="dqm__stat-icon" />
        <div class="dqm__stat-count">{{ card.count }}</div>
        <div class="dqm__stat-label">{{ card.label }}</div>
      </div>
    </div>

    <!-- ── Filters ─────────────────────────────────────────────── -->
    <div class="dqm__filters">

      <!-- Быстрый поиск -->
      <q-input
        v-model="searchQuery"
        dense outlined clearable
        placeholder="Поиск по ID, ЖК, ошибке…"
        class="dqm__search-input"
        @clear="searchQuery = ''"
      >
        <template v-slot:prepend><q-icon name="search" size="16px" color="grey-5" /></template>
      </q-input>

      <q-separator vertical inset style="margin: 2px 4px" />

      <!-- ЖК -->
      <q-select
        v-model="filterResident"
        :options="residentOptions"
        option-value="value" option-label="label"
        emit-value map-options
        dense outlined clearable
        label="ЖК"
        use-input input-debounce="150"
        @filter="filterResidentOptions"
        class="dqm__filter-select dqm__filter-select--md"
        @update:model-value="onResidentChange"
      >
        <template v-slot:no-option>
          <q-item><q-item-section class="text-grey">Не найдено</q-item-section></q-item>
        </template>
      </q-select>

      <!-- Тип проекта -->
      <q-select
        v-model="filterProjectType"
        :options="projectTypeOptions"
        option-value="value" option-label="label"
        emit-value map-options
        dense outlined clearable
        label="Тип проекта"
        :disable="!filterResident"
        class="dqm__filter-select dqm__filter-select--md"
        @update:model-value="onProjectTypeChange"
      />

      <!-- Подъезд -->
      <q-select
        v-model="filterEntrance"
        :options="entranceOptions"
        option-value="value" option-label="label"
        emit-value map-options
        dense outlined clearable
        label="Подъезд"
        :disable="!filterProjectType"
        class="dqm__filter-select"
        @update:model-value="onFilterChange"
      />

      <q-separator vertical inset style="margin: 2px 4px" />

      <!-- Статус -->
      <q-select
        v-model="filterStatus"
        :options="statusOptions"
        option-value="value" option-label="label"
        emit-value map-options
        dense outlined clearable
        label="Статус"
        class="dqm__filter-select dqm__filter-select--sm"
        @update:model-value="onFilterChange"
      />

      <!-- Тип документа -->
      <q-select
        v-model="filterType"
        :options="typeOptions"
        option-value="value" option-label="label"
        emit-value map-options
        dense outlined clearable
        label="Тип документа"
        class="dqm__filter-select dqm__filter-select--md"
        @update:model-value="onFilterChange"
      />

      <!-- Даты -->
      <q-input v-model="filterDateFrom" type="date" dense outlined label="С"
        class="dqm__filter-date" @update:model-value="onFilterChange" />
      <q-input v-model="filterDateTo" type="date" dense outlined label="По"
        class="dqm__filter-date" @update:model-value="onFilterChange" />

    </div>

    <!-- ── Search badge ────────────────────────────────────────── -->
    <div v-if="searchQuery" class="dqm__search-hint">
      <q-icon name="manage_search" size="14px" class="q-mr-xs" />
      Поиск: <b>«{{ searchQuery }}»</b> — найдено {{ filteredItems.length }} из {{ items.length }} на странице
    </div>

    <!-- ── Bulk action bar ─────────────────────────────────────── -->
    <transition name="dqm-bar">
      <div v-if="selectedIds.size" class="dqm__bulk-bar">
        <span class="dqm__bulk-info">Выбрано: <b>{{ selectedIds.size }}</b></span>
        <q-btn
          v-if="hasSelectedFailed"
          outline size="sm" color="warning" icon="replay"
          :label="`Регенерировать (${selectedFailedCount})`"
          :loading="bulkRegenLoading"
          @click="onBulkRegenerate"
        />
        <q-btn
          outline size="sm" color="negative" icon="delete"
          :label="`Удалить (${selectedIds.size})`"
          :loading="bulkDeleteLoading"
          @click="confirmBulkDelete"
        />
        <q-btn flat size="sm" color="grey-7" label="Снять выделение" @click="clearSelection" />
      </div>
    </transition>

    <!-- ── Table ───────────────────────────────────────────────── -->
    <div class="dqm__table-wrap">
      <table class="dqm__table">
        <thead>
          <tr>
            <th class="dqm__th dqm__th--check">
              <q-checkbox
                :model-value="allCurrentSelected"
                :indeterminate="someCurrentSelected"
                dense size="sm"
                @update:model-value="toggleSelectAll"
              />
            </th>
            <th class="dqm__th dqm__th--id">ID</th>
            <th class="dqm__th">Проект</th>
            <th class="dqm__th">Тип / Документ</th>
            <th class="dqm__th">Статус задачи</th>
            <th class="dqm__th">Даты</th>
            <th class="dqm__th dqm__th--error">Ошибка</th>
            <th class="dqm__th dqm__th--actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && !items.length">
            <td colspan="8" class="dqm__td dqm__td--empty">
              <q-spinner color="primary" size="22px" /><span class="q-ml-sm">Загрузка...</span>
            </td>
          </tr>
          <tr v-else-if="!filteredItems.length">
            <td colspan="8" class="dqm__td dqm__td--empty">
              <template v-if="searchQuery">Ничего не найдено по запросу «{{ searchQuery }}»</template>
              <template v-else>Нет данных</template>
            </td>
          </tr>

          <tr
            v-for="row in filteredItems" :key="row.document_queue_id"
            class="dqm__tr"
            :class="{
              [`dqm__tr--${row.status.toLowerCase().replace('_','-')}`]: true,
              'dqm__tr--selected': selectedIds.has(row.document_queue_id),
            }"
          >
            <!-- Checkbox -->
            <td class="dqm__td dqm__td--check">
              <q-checkbox
                :model-value="selectedIds.has(row.document_queue_id)"
                dense size="sm"
                @update:model-value="(v) => toggleSelect(row, v)"
              />
            </td>

            <!-- ID -->
            <td class="dqm__td dqm__td--id">
              <span class="dqm__id-pill">#{{ row.document_queue_id }}</span>
            </td>

            <!-- Проект -->
            <td class="dqm__td dqm__td--project">
              <template v-if="row.resident_name">
                <div class="dqm__project-name">{{ row.resident_name }}</div>
                <div v-if="row.project_type_name" class="dqm__project-type">{{ row.project_type_name }}</div>
                <div v-if="row.entrance_label" class="dqm__project-entrance">
                  <q-icon name="door_front" size="10px" class="q-mr-xs" />{{ row.entrance_label }}
                </div>
              </template>
              <span v-else class="text-grey-5">—</span>
            </td>

            <!-- Тип / Doc ID -->
            <td class="dqm__td dqm__td--type">
              <span class="dqm__type-badge" :class="`dqm__type-badge--${row.type_code === 'HELP_CALL' ? 'hc' : 'fmd'}`">
                {{ row.type_code === 'HELP_CALL' ? 'Вызов ОКК' : 'Планировка' }}
              </span>
              <div class="dqm__docid">
                {{ row.type_code === 'HELP_CALL' ? `HC #${row.help_call_id}` : `FMD #${row.floor_map_document_id}` }}
              </div>
            </td>

            <!-- Статус задачи (статус + операция + попытки) -->
            <td class="dqm__td dqm__td--status">
              <div class="dqm__status-cell">
                <!-- Статус -->
                <span class="dqm__status-badge" :class="`dqm__status-badge--${row.status}`">
                  <q-icon :name="statusIcon(row.status)" size="11px" class="q-mr-xs" />
                  {{ statusLabel(row.status) }}
                </span>
                <!-- Операция + Попытки -->
                <div class="dqm__status-meta">
                  <span class="dqm__op-badge" :class="`dqm__op-badge--${row.operation_type}`">
                    {{ row.operation_type === 'REGENERATE' ? '↻ Переформировать' : '▶ Первичная' }}
                  </span>
                  <span
                    class="dqm__attempts"
                    :class="{
                      'dqm__attempts--warn': row.attempt_count >= row.max_attempts,
                      'dqm__attempts--ok':   row.attempt_count < row.max_attempts && row.attempt_count > 0,
                    }"
                  >
                    <q-icon name="loop" size="10px" class="q-mr-xs" />{{ row.attempt_count }} / {{ row.max_attempts }} поп.
                  </span>
                </div>
              </div>
            </td>

            <!-- Даты -->
            <td class="dqm__td dqm__td--dates">
              <div class="dqm__dates-cell">
                <div class="dqm__date-row dqm__date-row--created">
                  <q-icon name="add_circle_outline" size="11px" />
                  <span>{{ fmt(row.created_at) }}</span>
                </div>
                <div v-if="row.completed_at" class="dqm__date-row dqm__date-row--completed">
                  <q-icon name="check_circle" size="11px" />
                  <span>{{ fmt(row.completed_at) }}</span>
                </div>
                <div v-else-if="row.started_at" class="dqm__date-row dqm__date-row--started">
                  <q-icon name="autorenew" size="11px" />
                  <span>{{ fmt(row.started_at) }}</span>
                </div>
              </div>
            </td>

            <!-- Ошибка -->
            <td class="dqm__td dqm__td--error">
              <template v-if="row.error_message">
                <div class="dqm__error-text" :title="row.error_message">
                  {{ row.error_message }}
                </div>
              </template>
              <span v-else class="text-grey-4">—</span>
            </td>

            <!-- Действия -->
            <td class="dqm__td dqm__td--actions">
              <div class="dqm__row-actions">
                <q-btn flat dense round color="primary" icon="replay" size="xs"
                  :loading="regenLoadingIds.has(row.document_queue_id)"
                  @click="onRegenerate(row)">
                  <q-tooltip>Регенерировать</q-tooltip>
                </q-btn>
                <q-btn flat dense round color="negative" icon="delete" size="xs"
                  :loading="deleteLoadingIds.has(row.document_queue_id)"
                  @click="confirmDelete(row)">
                  <q-tooltip>Удалить</q-tooltip>
                </q-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Pagination ──────────────────────────────────────────── -->
    <div class="dqm__pagination">
      <div class="dqm__pagination-info">
        {{ items.length ? offset + 1 : 0 }}–{{ offset + items.length }} из {{ total }}
        <span v-if="searchQuery && filteredItems.length !== items.length" class="dqm__pagination-search">
          (показано {{ filteredItems.length }} после поиска)
        </span>
      </div>
      <div class="dqm__pagination-controls">
        <q-btn flat dense icon="first_page" :disable="page <= 1 || loading" @click="changePage(1)" />
        <q-btn flat dense icon="chevron_left" :disable="page <= 1 || loading" @click="changePage(page - 1)" />
        <span class="dqm__pagination-page">{{ page }} / {{ totalPages }}</span>
        <q-btn flat dense icon="chevron_right" :disable="page >= totalPages || loading" @click="changePage(page + 1)" />
        <q-btn flat dense icon="last_page" :disable="page >= totalPages || loading" @click="changePage(totalPages)" />
        <q-select
          v-model="pageSize"
          :options="[20, 50, 100]"
          dense outlined
          style="width: 76px"
          @update:model-value="onFilterChange"
        />
      </div>
    </div>

    <!-- ── Confirm delete dialog ───────────────────────────────── -->
    <q-dialog v-model="confirmDialogOpen" persistent>
      <q-card style="min-width: 340px">
        <q-card-section class="row items-center">
          <q-icon name="warning" color="negative" size="28px" class="q-mr-sm" />
          <div>
            <div class="text-h6">Подтверждение удаления</div>
            <div class="text-grey-7 text-sm q-mt-xs">{{ confirmDialogText }}</div>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey-7" v-close-popup />
          <q-btn flat label="Удалить" color="negative" :loading="confirmDeleting" @click="executeDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import { documentQueueAPI } from './services/api'

const { showSuccessSnackbar, showErrorSnackbar } = useCustomSnackbar()

// ─── Loading states ──────────────────────────────────────────────────────────
const loading          = ref(false)
const bulkRegenLoading = ref(false)
const bulkDeleteLoading = ref(false)
const confirmDeleting  = ref(false)
const regenLoadingIds  = reactive(new Set())
const deleteLoadingIds = reactive(new Set())

// ─── Data ────────────────────────────────────────────────────────────────────
const items      = ref([])
const total      = ref(0)
const page       = ref(1)
const pageSize   = ref(50)
const statCounts = ref({})

// ─── Filters ─────────────────────────────────────────────────────────────────
const searchQuery       = ref('')
const filterStatus      = ref(null)
const filterType        = ref(null)
const filterDateFrom    = ref(null)
const filterDateTo      = ref(null)
const filterResident    = ref(null)
const filterProjectType = ref(null)
const filterEntrance    = ref(null)

// ─── Quick search (client-side на текущей странице) ───────────────────────────
const filteredItems = computed(() => {
  const q = searchQuery.value?.trim().toLowerCase()
  if (!q) return items.value
  return items.value.filter((r) =>
    String(r.document_queue_id).includes(q) ||
    r.resident_name?.toLowerCase().includes(q) ||
    r.entrance_label?.toLowerCase().includes(q) ||
    r.project_type_name?.toLowerCase().includes(q) ||
    r.error_message?.toLowerCase().includes(q) ||
    String(r.help_call_id || '').includes(q) ||
    String(r.floor_map_document_id || '').includes(q)
  )
})

// ─── Cascade filter options ───────────────────────────────────────────────────
const rawFilterOptions = ref([])

const allResidents = computed(() => {
  const seen = new Set()
  return rawFilterOptions.value
    .filter((r) => {
      if (seen.has(r.resident_id)) return false
      seen.add(r.resident_id)
      return true
    })
    .map((r) => ({ value: r.resident_id, label: r.resident_name }))
    .sort((a, b) => a.label.localeCompare(b.label))
})
const residentOptions = ref([])

const projectTypeOptions = computed(() => {
  if (!filterResident.value) return []
  const seen = new Set()
  return rawFilterOptions.value
    .filter((r) => r.resident_id === filterResident.value && !seen.has(r.project_type_id) && seen.add(r.project_type_id))
    .map((r) => ({ value: r.project_type_id, label: r.project_type_name }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const entranceOptions = computed(() => {
  if (!filterResident.value || !filterProjectType.value) return []
  return rawFilterOptions.value
    .filter((r) => r.resident_id === filterResident.value && r.project_type_id === filterProjectType.value)
    .map((r) => ({ value: r.project_entrance_id, label: r.entrance_label }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const filterResidentOptions = (val, update) => {
  update(() => {
    const q = val.toLowerCase()
    residentOptions.value = !q
      ? allResidents.value
      : allResidents.value.filter((o) => o.label.toLowerCase().includes(q))
  })
}

// ─── Selection ────────────────────────────────────────────────────────────────
const selectedIds  = reactive(new Set())
const selectedRows = reactive(new Map())

const toggleSelect = (row, val) => {
  if (val) {
    selectedIds.add(row.document_queue_id)
    selectedRows.set(row.document_queue_id, row)
  } else {
    selectedIds.delete(row.document_queue_id)
    selectedRows.delete(row.document_queue_id)
  }
}

const toggleSelectAll = (val) => {
  if (val) {
    items.value.forEach((r) => {
      selectedIds.add(r.document_queue_id)
      selectedRows.set(r.document_queue_id, r)
    })
  } else {
    items.value.forEach((r) => {
      selectedIds.delete(r.document_queue_id)
      selectedRows.delete(r.document_queue_id)
    })
  }
}

const clearSelection = () => {
  selectedIds.clear()
  selectedRows.clear()
}

const allCurrentSelected = computed(
  () => items.value.length > 0 && items.value.every((r) => selectedIds.has(r.document_queue_id))
)
const someCurrentSelected = computed(
  () => !allCurrentSelected.value && items.value.some((r) => selectedIds.has(r.document_queue_id))
)
const hasSelectedFailed = computed(
  () => [...selectedIds].some((id) => selectedRows.get(id)?.status === 'FAILED')
)
const selectedFailedCount = computed(
  () => [...selectedIds].filter((id) => selectedRows.get(id)?.status === 'FAILED').length
)

// ─── Delete confirm dialog ────────────────────────────────────────────────────
const confirmDialogOpen   = ref(false)
const confirmDialogText   = ref('')
const confirmDeleteAction = ref(null)

const confirmDelete = (row) => {
  confirmDialogText.value   = `Удалить запись #${row.document_queue_id}?`
  confirmDeleteAction.value = async () => {
    deleteLoadingIds.add(row.document_queue_id)
    try {
      const res = await documentQueueAPI.delete([row.document_queue_id])
      showSuccessSnackbar(`Удалено: ${res?.deleted ?? 1}`)
      reload()
    } catch (e) {
      showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка удаления')
    } finally {
      deleteLoadingIds.delete(row.document_queue_id)
    }
  }
  confirmDialogOpen.value = true
}

const confirmBulkDelete = () => {
  const ids = [...selectedIds]
  confirmDialogText.value   = `Удалить ${ids.length} записей?`
  confirmDeleteAction.value = async () => {
    bulkDeleteLoading.value = true
    try {
      const res = await documentQueueAPI.delete(ids)
      showSuccessSnackbar(`Удалено: ${res?.deleted ?? ids.length}`)
      clearSelection()
      reload()
    } catch (e) {
      showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка удаления')
    } finally {
      bulkDeleteLoading.value = false
    }
  }
  confirmDialogOpen.value = true
}

const executeDelete = async () => {
  confirmDeleting.value = true
  try {
    await confirmDeleteAction.value?.()
  } finally {
    confirmDeleting.value   = false
    confirmDialogOpen.value = false
    confirmDeleteAction.value = null
  }
}

// ─── Options ──────────────────────────────────────────────────────────────────
const statusOptions = [
  { value: 'PENDING',     label: 'Ожидает'    },
  { value: 'IN_PROGRESS', label: 'В процессе' },
  { value: 'COMPLETED',   label: 'Завершено'  },
  { value: 'FAILED',      label: 'Ошибка'     },
]
const typeOptions = [
  { value: 'FLOOR_MAP_DOCUMENT', label: 'Планировка (FMD)' },
  { value: 'HELP_CALL',          label: 'Вызов ОКК (HC)'   },
]

// ─── Computed ─────────────────────────────────────────────────────────────────
const offset     = computed(() => (page.value - 1) * pageSize.value)
const totalPages = computed(() => total.value > 0 ? Math.ceil(total.value / pageSize.value) : 1)

const statCards = computed(() => [
  { status: 'PENDING',     label: 'Ожидают',    cls: 'pending',     icon: 'schedule',    count: statCounts.value['PENDING']     || 0 },
  { status: 'IN_PROGRESS', label: 'В процессе', cls: 'in-progress', icon: 'autorenew',   count: statCounts.value['IN_PROGRESS'] || 0 },
  { status: 'COMPLETED',   label: 'Готово',     cls: 'completed',   icon: 'check_circle',count: statCounts.value['COMPLETED']   || 0 },
  { status: 'FAILED',      label: 'Ошибки',     cls: 'failed',      icon: 'error',       count: statCounts.value['FAILED']      || 0 },
])

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) => {
  if (!val) return '—'
  try {
    const d = new Date(val)
    const p = (n) => String(n).padStart(2, '0')
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
  } catch { return val }
}

const statusLabel = (s) =>
  ({ PENDING: 'Ожидает', IN_PROGRESS: 'В процессе', COMPLETED: 'Завершено', FAILED: 'Ошибка' }[s] || s)

const statusIcon = (s) =>
  ({ PENDING: 'schedule', IN_PROGRESS: 'autorenew', COMPLETED: 'check_circle', FAILED: 'error' }[s] || 'help')

// ─── Load ──────────────────────────────────────────────────────────────────────
const loadItems = async () => {
  if (loading.value) return
  loading.value = true
  clearSelection()
  try {
    const res = await documentQueueAPI.read({
      status:              filterStatus.value      || undefined,
      type_code:           filterType.value        || undefined,
      date_from:           filterDateFrom.value    || undefined,
      date_to:             filterDateTo.value      || undefined,
      resident_id:         filterResident.value    || undefined,
      project_type_id:     filterProjectType.value || undefined,
      project_entrance_id: filterEntrance.value    || undefined,
      limit:               pageSize.value,
      offset:              offset.value,
    })
    items.value = res?.items || []
    total.value = res?.total || 0
  } catch (e) {
    showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка загрузки')
  } finally {
    loading.value = false
  }
}

const loadCounts = async () => {
  try {
    const res = await documentQueueAPI.statusCounts()
    statCounts.value = res?.data || {}
  } catch { /* не критично */ }
}

const loadFilterOptions = async () => {
  try {
    const res = await documentQueueAPI.filterOptions()
    rawFilterOptions.value = res?.data || []
    residentOptions.value  = [...allResidents.value]
  } catch { /* не критично */ }
}

const reload = () => {
  loadItems()
  loadCounts()
}

// ─── Filters & pagination ──────────────────────────────────────────────────────
const onFilterChange = () => { page.value = 1; loadItems() }
const changePage = (p) => { if (p < 1 || p === page.value) return; page.value = p; loadItems() }

const onResidentChange = () => {
  filterProjectType.value = null
  filterEntrance.value    = null
  onFilterChange()
}
const onProjectTypeChange = () => {
  filterEntrance.value = null
  onFilterChange()
}

const clearFilters = () => {
  searchQuery.value       = ''
  filterStatus.value      = null
  filterType.value        = null
  filterDateFrom.value    = null
  filterDateTo.value      = null
  filterResident.value    = null
  filterProjectType.value = null
  filterEntrance.value    = null
  page.value = 1
  reload()
}

const toggleStatusFilter = (status) => {
  filterStatus.value = filterStatus.value === status ? null : status
  onFilterChange()
}

// ─── Regenerate ────────────────────────────────────────────────────────────────
const onRegenerate = async (row) => {
  const id = row.document_queue_id
  regenLoadingIds.add(id)
  try {
    await documentQueueAPI.regenerate({
      floor_map_document_id: row.floor_map_document_id || undefined,
      help_call_id:          row.help_call_id          || undefined,
    })
    showSuccessSnackbar('Задача добавлена в очередь')
    reload()
  } catch (e) {
    showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка регенерации')
  } finally {
    regenLoadingIds.delete(id)
  }
}

const onBulkRegenerate = async () => {
  const failedRows = [...selectedIds]
    .map((id) => selectedRows.get(id))
    .filter((r) => r?.status === 'FAILED')

  if (!failedRows.length) return
  bulkRegenLoading.value = true

  try {
    const res = await documentQueueAPI.regenerateBulk(
      failedRows.map((r) => ({
        floor_map_document_id: r.floor_map_document_id || undefined,
        help_call_id:          r.help_call_id          || undefined,
      }))
    )
    const ok   = res?.ok    || 0
    const errs = res?.errors?.length || 0
    if (errs === 0) showSuccessSnackbar(`${ok} задач добавлено в очередь`)
    else            showErrorSnackbar(`${ok} добавлено, ${errs} ошибок`)
    reload()
  } catch (e) {
    showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка')
  } finally {
    bulkRegenLoading.value = false
  }
}

// ─── Mount ────────────────────────────────────────────────────────────────────
onMounted(() => {
  loadItems()
  loadCounts()
  loadFilterOptions()
})
</script>

<style scoped lang="scss">
.dqm {
  display: flex;
  flex-direction: column;
  gap: 14px;

  // ── Header ──
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    font-size: 22px;
    font-weight: 600;
    color: #111827;
  }

  &__header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  // ── Stat cards ──
  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__stat-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 10px 10px;
    border-radius: 10px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.12s, box-shadow 0.15s;
    user-select: none;

    &:hover { transform: translateY(-2px); }
    &--active { box-shadow: 0 0 0 3px currentColor; transform: translateY(-2px); }

    &--pending     { background: #fffbeb; color: #92400e; border-color: #fde68a; }
    &--in-progress { background: #eff6ff; color: #1e40af; border-color: #bfdbfe; }
    &--completed   { background: #f0fdf4; color: #166534; border-color: #bbf7d0; }
    &--failed      { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
  }

  &__stat-icon {
    opacity: 0.55;
    margin-bottom: 4px;
  }

  &__stat-count {
    font-size: 26px;
    font-weight: 700;
    line-height: 1.1;
  }

  &__stat-label {
    font-size: 11px;
    font-weight: 500;
    margin-top: 3px;
    opacity: 0.75;
  }

  // ── Filters ──
  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  &__search-input {
    width: 240px;
    flex-shrink: 0;
  }

  &__filter-select {
    width: 130px;
    flex-shrink: 0;
    &--sm { width: 120px; }
    &--md { width: 160px; }
  }

  &__filter-date {
    width: 120px;
    flex-shrink: 0;
  }

  // ── Search hint ──
  &__search-hint {
    font-size: 12px;
    color: #6b7280;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 5px 12px;
    display: flex;
    align-items: center;
  }

  // ── Bulk bar ──
  &__bulk-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 8px;
    padding: 8px 14px;
  }

  &__bulk-info {
    font-size: 13px;
    color: #0369a1;
    margin-right: 4px;
  }

  // ── Table ──
  &__table-wrap {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  &__th {
    padding: 8px 10px;
    text-align: left;
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 1;

    &--check   { width: 32px; }
    &--id      { width: 52px; }
    &--error   { min-width: 180px; max-width: 300px; }
    &--actions { width: 68px; }
  }

  &__tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background 0.1s;
    &:hover { background: #f9fafb; }

    &--failed      { background: #fff5f5; &:hover { background: #fef2f2; } }
    &--in-progress { background: #f0f8ff; &:hover { background: #eff6ff; } }
    &--selected    { background: #eff6ff !important; }
  }

  &__td {
    padding: 7px 10px;
    vertical-align: middle;

    &--empty   { text-align: center; color: #9ca3af; padding: 32px; }
    &--check   { width: 32px; }
    &--id      { white-space: nowrap; }
    &--project { max-width: 180px; }
    &--type    { white-space: nowrap; }
    &--status  { min-width: 170px; }
    &--dates   { white-space: nowrap; min-width: 150px; }
    &--error   { max-width: 300px; }
    &--actions { text-align: center; white-space: nowrap; }
  }

  // ── ID pill ──
  &__id-pill {
    display: inline-block;
    background: #f3f4f6;
    color: #374151;
    border-radius: 4px;
    padding: 1px 6px;
    font-weight: 600;
    font-size: 11px;
    font-family: ui-monospace, monospace;
  }

  // ── Project cell ──
  &__project-name {
    font-size: 12px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }

  &__project-type {
    font-size: 11px;
    color: #6b7280;
    margin-top: 1px;
  }

  &__project-entrance {
    font-size: 11px;
    color: #2563eb;
    margin-top: 1px;
    display: flex;
    align-items: center;
  }

  // ── Type / Doc cell ──
  &__type-badge {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 4px;
    font-weight: 700;
    font-size: 11px;
    &--hc  { background: #dbeafe; color: #1e40af; }
    &--fmd { background: #e0e7ff; color: #3730a3; }
  }

  &__docid {
    font-family: ui-monospace, monospace;
    font-size: 10px;
    color: #9ca3af;
    margin-top: 3px;
  }

  // ── Status cell ──
  &__status-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__status-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    &--PENDING     { background: #fef3c7; color: #92400e; }
    &--IN_PROGRESS { background: #dbeafe; color: #1e40af; }
    &--COMPLETED   { background: #dcfce7; color: #166534; }
    &--FAILED      { background: #fee2e2; color: #991b1b; }
  }

  &__op-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    &--GENERATE   { background: #f3f4f6; color: #6b7280; }
    &--REGENERATE { background: #fef9c3; color: #78350f; }
  }

  &__attempts {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    color: #9ca3af;
    &--ok   { color: #16a34a; }
    &--warn { color: #dc2626; font-weight: 600; }
  }

  // ── Dates cell ──
  &__dates-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  &__date-row {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;

    &--created   { color: #9ca3af; }
    &--completed { color: #16a34a; font-weight: 500; }
    &--started   { color: #2563eb; }
  }

  // ── Error text ──
  &__error-text {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    color: #991b1b;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    cursor: default;
  }

  // ── Row actions ──
  &__row-actions {
    display: flex;
    gap: 2px;
    justify-content: center;
  }

  // ── Pagination ──
  &__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
    color: #6b7280;
  }

  &__pagination-search {
    margin-left: 6px;
    color: #2563eb;
  }

  &__pagination-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__pagination-page {
    min-width: 52px;
    text-align: center;
    font-weight: 600;
    color: #374151;
    font-size: 13px;
  }
}

.dqm-bar-enter-active,
.dqm-bar-leave-active { transition: all 0.2s ease; }
.dqm-bar-enter-from,
.dqm-bar-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
